import { useId, useState } from 'react';

import { Money } from '@betterment/core';
import { FormikField, HStack, Input, Label, Text, ToggleGroup, VStack } from '@betterment/samba-react';
import { useField } from 'formik';

import { amountToString, percentToString } from '~/b4b/utils/formatHelpers';

import { ContributionRateEditorValue } from './types';
import { createFormikFieldName, editorValueToString } from './utils';

import {
  PayrollContributionAmountType,
  PayrollContributionPercentType,
  PayrollContributionRateType,
} from '../graphql/types';

const editorValueTextField = 'text';
const editorValueTypeField = 'type';

export const ContributionRateEditor = ({
  formikFieldName,
  label,
  isSubmitting,
}: {
  formikFieldName: string;
  label: string;
  isSubmitting: boolean;
}) => {
  const labelId = useId();
  const inputId = useId();
  const inputName = createFormikFieldName(formikFieldName, editorValueTextField);
  const toggleName = createFormikFieldName(formikFieldName, editorValueTypeField);

  const [inputFieldProps, , inputFieldHelpers] =
    useField<ContributionRateEditorValue[typeof editorValueTextField]>(inputName);
  const [toggleFieldProps, , toggleFieldHelpers] =
    useField<ContributionRateEditorValue[typeof editorValueTypeField]>(toggleName);

  const [lastPercentValue, setLastPercentValue] = useState<string>(
    toggleFieldProps.value === 'PayrollContributionPercent' ? inputFieldProps.value : percentToString(0)
  );
  const [lastAmountValue, setLastAmountValue] = useState<string>(
    toggleFieldProps.value === 'PayrollContributionAmount' ? inputFieldProps.value : amountToString(Money(0))
  );

  let inputProps: { leadingSymbol: '$'; alignment: 'right' } | { trailingSymbol: '%'; alignment: 'left' };
  switch (toggleFieldProps.value) {
    case 'PayrollContributionPercent':
      inputProps = { trailingSymbol: '%', alignment: 'left' };
      break;
    case 'PayrollContributionAmount':
      inputProps = { leadingSymbol: '$', alignment: 'right' };
      break;
  }

  function setLastValue(value: string) {
    switch (toggleFieldProps.value) {
      case 'PayrollContributionPercent':
        setLastPercentValue(value);
        break;
      case 'PayrollContributionAmount':
        setLastAmountValue(value);
        break;
    }
  }

  function handleInputChange(event: React.FormEvent<HTMLInputElement>) {
    let disallowedCharactersRegex: RegExp;
    switch (toggleFieldProps.value) {
      case 'PayrollContributionPercent':
        disallowedCharactersRegex = /[^\d.]/g;
        break;
      case 'PayrollContributionAmount':
        disallowedCharactersRegex = /[^\d.,]/g;
        break;
    }

    const value = event.currentTarget.value.replace(disallowedCharactersRegex, '');

    void inputFieldHelpers.setValue(value);
    setLastValue(value);
  }

  function handleBlur() {
    const value = editorValueToString({ text: inputFieldProps.value, type: toggleFieldProps.value });

    void inputFieldHelpers.setValue(value);
    setLastValue(value);
  }

  function handleToggleChange(type: PayrollContributionRateType) {
    let lastValue: string;

    switch (type) {
      case 'PayrollContributionPercent':
        lastValue = lastPercentValue;
        break;
      case 'PayrollContributionAmount':
        lastValue = lastAmountValue;
        break;
    }

    void inputFieldHelpers.setValue(lastValue);
    void toggleFieldHelpers.setValue(type);
  }

  return (
    <FormikField name={inputName}>
      <HStack role="group" aria-describedby={labelId} css={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Label id={labelId} htmlFor={inputId}>
          {label}
        </Label>
        <VStack css={{ gap: 'space.150' }}>
          <HStack css={{ gap: 'space.150', justifyContent: 'flex-end' }}>
            <Input
              id={inputId}
              {...inputProps}
              value={inputFieldProps.value}
              onChange={handleInputChange}
              onBlur={handleBlur}
              css={{ width: '150px' }}
              disabled={isSubmitting}
            />
            <ToggleGroup
              type="single"
              spacing="default"
              value={toggleFieldProps.value}
              onValueChange={handleToggleChange}
              disabled={isSubmitting}
            >
              <ToggleGroup.Item
                value={PayrollContributionPercentType}
                css={{ px: 'space.200' }}
                disabled={toggleFieldProps.value === PayrollContributionPercentType}
              >
                <Text>%</Text>
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value={PayrollContributionAmountType}
                css={{ px: 'space.200' }}
                disabled={toggleFieldProps.value === PayrollContributionAmountType}
              >
                <Text>$</Text>
              </ToggleGroup.Item>
            </ToggleGroup>
          </HStack>
          <FormikField.ErrorMessage />
        </VStack>
      </HStack>
    </FormikField>
  );
};
